<script context="module" lang="ts">
	export function isBetween(day: Date, range: Interval | undefined) {
		if (!range) return false;
		try {
			return (
				isSameDay(day, range.start) ||
				isSameDay(day, range.end) ||
				isWithinInterval(day, range)
			);
		} catch (e) {
			return false;
		}
	}

</script>

<script lang="ts">
	import { createEventDispatcher, onMount } from "svelte";
	import {
		max,
		min,
		addDays,
		addYears,
		isValid,
		isWithinInterval,
		eachDayOfInterval,
		eachMonthOfInterval,
		endOfMonth,
		format as dFormat,
		getDate,
		isSameDay,
		parse,
		startOfMonth,
		startOfWeek,
	} from "date-fns";
	import type { Interval } from "date-fns";

	/* BASE */

	const dispatch = createEventDispatcher();

	export let date: Date = new Date();
	export let allowFuture: boolean = true;
	export let startDate: Date = addYears(date, -2);
	export let endDate: Date = addYears(date, allowFuture ? 0 : +2);
	export let rangeMode: boolean = false;
	export let editStart = true;
	export let format = dFormat;
	export let action = (node: HTMLElement) => {};

	let scrollElement: HTMLDivElement;
	let dateElements: Record<string, HTMLButtonElement> = {};
	let months = eachMonthOfInterval({ start: startDate, end: endDate });

	const weekdays = Array.from(Array(7)).map((_, i) =>
		addDays(startOfWeek(date), i)
	);

	function weeksOfMonth(month: Date): Date[][] {
		const startDay = startOfMonth(month);
		const endDay = endOfMonth(month);
		const days = eachDayOfInterval({
			start: startDay,
			end: endDay,
		});
		const weekMap: Record<string, Date[]> = {};
		for (let date of days) {
			const start = startOfWeek(date).toString();
			if (!weekMap[start]) weekMap[start] = [];
			weekMap[start].push(date);
		}
		return Object.values(weekMap);
	}

	$: monthWeekMap = months.reduce((acc, month) => {
		acc[month.toString()] = weeksOfMonth(month);
		return acc;
	}, {} as Record<string, Date[][]>);

	onMount(() => {
		scrollElement.scrollTop =
			dateElements[format(date, "yyyy-MM-dd")].offsetTop;
	});

	/* Single date select */

	export let selectedDate: Date | undefined = undefined;
	$: displayDate = selectedDate ? format(selectedDate, "yyyy.MM.dd") : "";

	/* Range select */

	export let range = {
		start: new Date(),
		end: new Date(),
	};

	// true = editing start, false = editing end
	$: displayRange = {
		start: isValid(range.start) ? format(range.start, "yyyy.MM.dd") : "",
		end: isValid(range.end) ? format(range.end, "yyyy.MM.dd") : "",
	};

	/* Common */

	function setRange(newRange: { start: Date; end: Date }) {
		dispatch("change", newRange);
	}

	function setEither(newDate: Date) {
		// setEither handles cases which should always expand the range for better UX
		// returns the true if the start was edited, false if the range.end was edited;
		setRange({
			start: min([range.start, newDate]),
			end: max([range.end, newDate]),
		});
		return isSameDay(range.start, newDate);
	}

	function setStart(newStart: Date) {
		setRange({
			start: newStart,
			end: max([range.end, newStart]),
		});
	}

	function setEnd(newEnd: Date) {
		setRange({
			start: min([range.start, newEnd]),
			end: newEnd,
		});
	}

	function onClick(e: Event) {
		const target = e.currentTarget as HTMLElement;
		const date = new Date(target.dataset.date as string);
		if (rangeMode && isSameDay(range.start, range.end)) {
			editStart = !setEither(date);
			return;
		}
		if (rangeMode && editStart) {
			setStart(date);
			editStart = false;
			return;
		}
		if (rangeMode && !editStart) {
			setEnd(date);
			editStart = true;
			return;
		}
		selectedDate = date;
	}

	function onTextChange(previousValue: string, setter: (date: Date) => void) {
		return function (e: Event) {
			const target = e.currentTarget as HTMLInputElement;
			const v = parse(target.value, "yyyy.MM.dd", new Date());
			if (!isValid(v)) {
				// if manually inputed date value is not valid,
				// then return it to the previous value of the displayDate,
				// which will restore it to the state before editing
				target.value = previousValue;
				return;
			}
			setter(v);
		};
	}

</script>

<div class="calendar" use:action>
	<!-- calendar head -->
	<div class="calendar-header">
		<input
			type="text"
			id="start-range-input"
			on:change={onTextChange(displayRange.start, setStart)}
			value={displayRange.start} />
		<span>~</span>
		<input
			type="text"
			id="end-range-input"
			value={displayRange.end}
			on:change={onTextChange(displayRange.end, setEnd)} />
	</div>
	<!-- calendar week day -->
	<div class="calendar-header-weekdays">
		{#each weekdays as day}
			<div class="week-item">{format(day, 'EEE')}</div>
		{/each}
	</div>
	<!-- calendar body -->
	<div class="calendar-body" bind:this={scrollElement}>
		{#each months as month}
			<div class="calendar-month">
				<div class="calendar-month-header">{format(month, 'yyyy MMM')}</div>
				{#if monthWeekMap[month.toString()]}
					{#each monthWeekMap[month.toString()] as week, index}
						<div class="calendar-week" class:is-first={index == 0}>
							{#each week as day}
								<button
									bind:this={dateElements[format(day, 'yyyy-MM-dd')]}
									class="calendar-day"
									class:is-date={isSameDay(day, date)}
									class:is-start={isSameDay(day, range.start || new Date(NaN))}
									class:is-end={isSameDay(day, range.end || new Date(NaN))}
									class:is-between={rangeMode && isBetween(day, range)}
									class:is-marker={rangeMode ? (editStart ? isSameDay(day, range.start) : isSameDay(day, range.end)) : selectedDate && isSameDay(day, selectedDate)}
									data-date={day.toString()}
									on:click={onClick}>
									<span class="calendar-marker"> {getDate(day)} </span>
								</button>
							{/each}
						</div>
					{/each}
				{/if}
			</div>
		{/each}
	</div>
</div>

<style lang="scss">
	.calendar {
		width: 350px;
		height: auto;
		padding: 20px 0px;
		border-radius: 5px;
		box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.16);
		background-color: #fff;

		.calendar-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 0px 10px;
			span {
				font-weight: 550;
				font-size: 1.25rem;
			}

			input {
				width: 156px;
				box-sizing: border-box;
				border: none;
				padding-bottom: 5px;
				border-bottom: 1px solid transparent;
				transition: border-bottom 0.3s;
				font-size: 1.5rem;
				font-weight: 500;
				border-radius: 0px;
				color: #46474b;

				&:focus {
					outline: none;
					border-bottom: 1px solid #6b93f3;
				}
				&#end-range-input {
					text-align: right;
				}
			}
		}
		.calendar-header-weekdays {
			display: flex;

			.week-item {
				color: #6d6f76;
			}
		}

		.week-item {
			display: flex;
			height: 40px;
			box-sizing: border-box;
			min-width: calc(100% / 7);
			max-width: calc(100% / 7);
			justify-content: center;
			align-items: center;
		}
	}

	.calendar-body {
		overflow: auto;
		box-sizing: border-box;
		width: 100%;
		max-height: 28em;
		-ms-overflow-style: none;
		scrollbar-width: none;
		&::-webkit-scrollbar {
			display: none;
		}

		.calendar-month {
			display: flex;
			flex-direction: column;
			flex-wrap: wrap;
			margin-top: 32px;
		}

		.calendar-month-header {
			color: #46474b;
			font-size: 1.25rem;
			font-weight: 500;
			padding-left: 16px;
		}

		.calendar-week {
			display: inline-flex;
			flex-direction: row;
			margin-top: 5px;

			&.is-first {
				justify-content: flex-end;
			}
		}
		.calendar-day {
			position: relative;
			z-index: 999;
			display: flex;
			height: 40px;
			width: calc(100% / 7);
			min-width: calc(100% / 7);
			max-width: calc(100% / 7);
			box-sizing: border-box;
			align-items: center;
			justify-content: center;
			border: 0;
			background: none;
			border-radius: 999px;
			font-size: 1em;
			outline: 0;
			color: #6d6f76;
			font-weight: 200;
			&:after {
				position: absolute;
				z-index: -999;
				width: 40px;
				height: 40px;
				top: 50%;
				left: 50%;
				transform: translate(-50%, -50%);
				border-radius: 999px;
				content: "";
			}
			&.is-marker:after {
				background-color: var(--signature-blue);
			}

			&:hover:after {
				box-shadow: var(--shadow-medium);
			}
			&:hover {
				color: #46474b;
				font-weight: 500;
			}

			&.is-date {
				font-weight: 500;
				color: #46474b;
			}

			&.is-marker {
				color: white;
				font-weight: 500;
			}

			&.is-between:before {
				position: absolute;
				z-index: -1000;
				top: 0;
				right: 0;
				bottom: 0;
				left: 0;
				background-color: #f0f0f4;
				content: "";
			}
			&.is-marker.is-between.is-start:before {
				left: 50%;
			}

			&.is-marker.is-between.is-end:before {
				right: 50%;
			}
		}
	}

</style>
